/**
 * External Chart Editor Page
 * ===========================
 * Full chart editing for external systems without authentication.
 * Uses the same chartStore as main editor for full functionality.
 * 
 * Features:
 * - Full chart editing with tooth selection
 * - Site measurements (pocket depths, margins, bleeding, suppuration)
 * - Tooth status, mobility, furcation
 * - Restorative options (crowns, fillings, implants)
 * - Voice input (AI and manual)
 * - Clinical notes
 * - Autosave
 * - Finalize chart
 */

import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Save,
  CheckCircle,
  Mic,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { useChartStore } from '@/stores/chartStore';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ToothGrid } from '@/components/charting/ToothGrid';
import { SiteInput } from '@/components/charting/SiteInput';
import { ChartControls } from '@/components/charting/ChartControls';
import { VoiceInputBar } from '@/components/charting/VoiceInputBar';
import { VoiceRecordingPanel } from '@/components/charting/VoiceRecordingPanel';
import { ParsedVoiceData } from '@/hooks/useVoiceInput';
import { formatDateTime } from '@/lib/utils';

export default function ExternalChartEditor() {
  const { chartId } = useParams<{ chartId: string }>();
  
  const {
    chart,
    isLoading,
    isSaving,
    isDirty,
    error,
    selectedTooth,
    lastAutosaveAt,
    setClinicalNotes,
    updateToothData,
    updateSiteData,
    selectTooth,
    setAAPClassification,
    setRiskFactor,
    setReferral,
    reset,
  } = useChartStore();

  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [showVoiceRecording, setShowVoiceRecording] = useState(false);
  const [isExternalLoading, setIsExternalLoading] = useState(true);
  const [patientData, setPatientData] = useState<any>(null);
  
  // Add ref to prevent rapid double-toggles
  const lastToggleTimeRef = useRef(0);

  // Load chart from public API (no auth required)
  useEffect(() => {
    const fetchAndLoadChart = async () => {
      if (!chartId) {
        setIsExternalLoading(false);
        return;
      }

      try {
        setIsExternalLoading(true);
        
        // Fetch from public endpoint
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/charts/${chartId}/public`
        );

        if (!response.ok) {
          throw new Error('Failed to load chart');
        }

        const chartData = await response.json();
        
        console.log('External editor: Chart loaded from API:', chartData);
        console.log('External editor: Tooth data count:', chartData.tooth_data?.length || 0);
        
        // If finalized, redirect to view mode
        if (chartData.status === 'finalized') {
          window.location.href = `/external/charts/${chartId}?external=true`;
          return;
        }

        // CRITICAL: Initialize tempToothData from loaded chart
        // This is what ToothGrid reads from!
        const tempToothData: Record<number, any> = {};
        if (chartData.tooth_data && Array.isArray(chartData.tooth_data)) {
          chartData.tooth_data.forEach((tooth: any) => {
            tempToothData[tooth.tooth_number] = { ...tooth };
            
            // Log loaded tooth data
            console.log(`External editor: Loaded tooth ${tooth.tooth_number}:`, {
              status: tooth.status,
              mobility: tooth.mobility,
              furcation: tooth.furcation,
              sites_count: tooth.sites?.length || 0,
              has_restorative: !!tooth.restorative,
            });
          });
        }

        console.log('External editor: Initialized tempToothData with', Object.keys(tempToothData).length, 'teeth');

        // Initialize chart store with data AND tempToothData
        useChartStore.setState({
          chart: chartData,
          tempToothData, // CRITICAL: This is what ToothGrid reads from!
          isLoading: false,
          isDirty: false,
          error: null,
          lastSavedAt: new Date(),
        });

        // Fetch patient data if available
        if (chartData.patient_id) {
          try {
            const patientResponse = await fetch(
              `${import.meta.env.VITE_API_URL}/api/v1/patients/${chartData.patient_id}/public`
            );
            if (patientResponse.ok) {
              const patient = await patientResponse.json();
              setPatientData(patient);
            }
          } catch (err) {
            console.warn('Failed to fetch patient data:', err);
          }
        }
      } catch (err) {
        console.error('Error loading chart:', err);
        useChartStore.setState({
          error: err instanceof Error ? err.message : 'Failed to load chart',
          isLoading: false,
        });
      } finally {
        setIsExternalLoading(false);
      }
    };

    fetchAndLoadChart();

    // Cleanup on unmount
    return () => {
      reset();
    };
  }, [chartId, reset]);

  // Override save to use external endpoint
  const handleSave = async () => {
    if (!chart || !chartId) {
      console.log('External editor: Cannot save - no chart or chartId');
      return;
    }

    if (isSaving) {
      console.log('External editor: Already saving, skipping');
      return;
    }

    console.log('External editor: Starting save for chart:', chartId);
    console.log('External editor: Current chart data:', {
      tooth_data_count: chart.tooth_data?.length || 0,
      clinical_notes_length: chart.clinical_notes?.length || 0,
    });

    try {
      useChartStore.setState({ isSaving: true });

      // Get tooth data from tempToothData in store (this has the latest changes)
      const { tempToothData } = useChartStore.getState();
      const tooth_data = Object.values(tempToothData);

      console.log('External editor: Saving tooth data:', tooth_data.length, 'teeth');
      
      const payload = {
        tooth_data,
        clinical_notes: chart.clinical_notes,
        exam_date: chart.exam_date,
        exam_type: chart.exam_type,
        // AAP Classification
        aap_stage: chart.aap_stage,
        aap_grade: chart.aap_grade,
        aap_extent: chart.aap_extent,
        // Risk Factors
        risk_factors: chart.risk_factors,
        // Referral
        referral_recommended: chart.referral_recommended,
        referral_reason: chart.referral_reason,
      };

      console.log('External editor: Sending payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/charts/${chartId}/external`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('External editor: Save failed:', response.status, errorText);
        throw new Error(`Failed to save chart: ${response.status}`);
      }

      const savedChart = await response.json();
      console.log('External editor: Save successful, response:', savedChart);
      
      // Rebuild tempToothData from saved response
      const newTempToothData: Record<number, any> = {};
      if (savedChart.tooth_data && Array.isArray(savedChart.tooth_data)) {
        savedChart.tooth_data.forEach((tooth: any) => {
          newTempToothData[tooth.tooth_number] = { ...tooth };
        });
        console.log('External editor: Synced tempToothData with', Object.keys(newTempToothData).length, 'teeth');
      }
      
      useChartStore.setState({
        chart: { ...chart, ...savedChart },
        tempToothData: newTempToothData, // Sync tempToothData with backend response
        isDirty: false,
        lastSavedAt: new Date(),
        lastAutosaveAt: new Date(),
        isSaving: false,
      });
      
      toast.success('Chart saved successfully');
    } catch (err) {
      console.error('External editor: Save error:', err);
      toast.error('Failed to save chart');
      useChartStore.setState({ isSaving: false });
    }
  };

  // Override finalize to use external endpoint
  const handleFinalize = async () => {
    if (!chart || !chartId) return;

    try {
      useChartStore.setState({ isSaving: true });

      // Save first
      await handleSave();

      // Then finalize
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/charts/${chartId}/external/finalize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to finalize chart');
      }

      toast.success('Chart finalized successfully');
      // Redirect to view mode
      window.location.href = `/external/charts/${chartId}?external=true`;
    } catch (err) {
      console.error('Error finalizing chart:', err);
      toast.error('Failed to finalize chart');
    } finally {
      useChartStore.setState({ isSaving: false });
      setShowFinalizeModal(false);
    }
  };

  // Auto-save every 10 seconds
  useEffect(() => {
    if (!isDirty || !chart || isSaving) return;

    console.log('External editor: Setting up auto-save timer for chart:', chart.id);
    const timer = setTimeout(() => {
      console.log('External editor: Auto-save triggered');
      handleSave();
    }, 10000);

    return () => clearTimeout(timer);
  }, [isDirty, chart, isSaving]);

  // Watch for any chart changes and mark as dirty
  useEffect(() => {
    if (!chart) return;
    
    // Log chart changes
    console.log('External editor: Chart loaded or updated');
  }, [chart]);

  const handleVoiceToggle = () => {
    const now = Date.now();
    const timeSinceLastToggle = now - lastToggleTimeRef.current;
    
    console.log('[External] ========== handleVoiceToggle CALLED ==========');
    console.log('[External] Time since last toggle:', timeSinceLastToggle, 'ms');
    console.log('[External] Current voiceEnabled state:', voiceEnabled);
    
    // Prevent toggles within 500ms of each other (debounce)
    if (timeSinceLastToggle < 500) {
      console.log('[External] BLOCKED - Ignoring rapid toggle - only', timeSinceLastToggle, 'ms since last toggle');
      return;
    }
    
    lastToggleTimeRef.current = now;
    console.log('[External] Calling setVoiceEnabled to change from', voiceEnabled, 'to', !voiceEnabled);
    setVoiceEnabled((prev) => {
      const newValue = !prev;
      console.log('[External] setVoiceEnabled callback - prev:', prev, 'new:', newValue);
      return newValue;
    });
  };

  const handleVoiceDataParsed = (data: ParsedVoiceData) => {
    console.log('External editor: Voice data parsed:', data);
    
    // Handle voice data using store actions
    switch (data.type) {
      case 'site_readings':
        selectTooth(data.toothNumber);
        data.readings.forEach((reading) => {
          const siteData: any = {};
          if (reading.pocketDepth !== undefined) {
            siteData.pocket_depth = reading.pocketDepth;
          }
          if (reading.bleeding !== undefined) {
            siteData.bleeding = reading.bleeding;
          }
          updateSiteData(data.toothNumber, reading.site as any, siteData);
        });
        break;
      
      case 'clinical_notes':
        setClinicalNotes(data.notes);
        break;
      
      case 'aap_classification': {
        // Get current values and update only what's provided
        const currentStage = chart?.aap_stage || '';
        const currentGrade = chart?.aap_grade || '';
        const currentExtent = chart?.aap_extent || '';
        
        setAAPClassification(
          data.stage || currentStage,
          data.grade || currentGrade,
          data.extent || currentExtent
        );
        break;
      }
      
      case 'risk_factor': {
        setRiskFactor(data.factor, data.enabled);
        break;
      }
      
      case 'referral': {
        setReferral(data.recommended, data.reason);
        break;
      }
      
      case 'tooth_status':
        selectTooth(data.toothNumber);
        updateToothData(data.toothNumber, { status: data.status });
        break;
      
      case 'mobility':
        selectTooth(data.toothNumber);
        updateToothData(data.toothNumber, { mobility: data.mobility });
        break;
      
      case 'furcation':
        selectTooth(data.toothNumber);
        updateToothData(data.toothNumber, { furcation: data.furcation });
        break;
    }
    
    // Trigger autosave
    setTimeout(() => {
      handleSave();
    }, 500);
  };

  // Voice recording submit handler for external access
  const handleVoiceRecordingSubmit = async (audioBlob: Blob, duration: number) => {
    if (!chart?.id || !chartId) {
      toast.error('Chart not loaded');
      return;
    }

    console.log('External editor: Uploading voice recording, size:', audioBlob.size, 'bytes, duration:', duration, 'seconds');
    
    const loadingToast = toast.loading('Uploading voice recording...');
    
    try {
      // Create FormData
      const formData = new FormData();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `voice_external_${chartId.substring(0, 8)}_${timestamp}.webm`;
      formData.append('audio_file', audioBlob, filename);

      console.log('External editor: Sending to /external/voice-input endpoint');

      // Upload to external endpoint (no authentication required)
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/charts/${chartId}/external/voice-input`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('External editor: Voice upload failed:', response.status, errorText);
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('External editor: Voice upload successful:', result);

      toast.success(
        `Recording processed! Found data for ${result.teeth_processed || 0} teeth`,
        { id: loadingToast }
      );

      // Close modal
      setShowVoiceRecording(false);

      // Reload chart to get the processed data
      console.log('External editor: Reloading chart to get processed data');
      const chartResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/charts/${chartId}/public`
      );

      if (chartResponse.ok) {
        const chartData = await chartResponse.json();
        
        // Rebuild tempToothData from reloaded chart
        const newTempToothData: Record<number, any> = {};
        if (chartData.tooth_data && Array.isArray(chartData.tooth_data)) {
          chartData.tooth_data.forEach((tooth: any) => {
            newTempToothData[tooth.tooth_number] = { ...tooth };
          });
        }

        useChartStore.setState({
          chart: chartData,
          tempToothData: newTempToothData,
          isDirty: false,
          lastSavedAt: new Date(),
        });

        toast.success('Chart updated with voice input data', { id: loadingToast });
      } else {
        toast.success('Recording processed. Please refresh to see changes.', { id: loadingToast });
      }
    } catch (err) {
      console.error('External editor: Error uploading voice recording:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to process voice recording',
        { id: loadingToast }
      );
    }
  };

  if (isExternalLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading chart editor...</p>
        </div>
      </div>
    );
  }

  if (error || !chart) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Card className="max-w-md">
          <div className="text-center p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600">{error || 'Failed to load chart'}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header with Actions */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Top Row: Title and Status */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Periodontal Chart</h1>
              {patientData && (
                <p className="text-sm text-gray-500 mt-1">
                  Patient: {patientData.full_name} • External Access
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="warning" size="md">
                {chart.status}
              </Badge>
              {lastAutosaveAt && (
                <span className="text-sm text-gray-500">
                  Saved {formatDateTime(lastAutosaveAt.toISOString())}
                </span>
              )}
              {isSaving && (
                <span className="text-sm text-blue-600">Saving...</span>
              )}
            </div>
          </div>

          {/* Bottom Row: Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Voice Input Bar - Real-time voice charting */}
            <VoiceInputBar
              onDataParsed={handleVoiceDataParsed}
              isEnabled={voiceEnabled}
              onToggle={handleVoiceToggle}
            />
            
            {/* AI Voice Recording Button */}
            <Button
              onClick={() => setShowVoiceRecording(true)}
              variant="outline"
              size="sm"
              disabled={voiceEnabled}
            >
              <Mic className="h-4 w-4 mr-2" />
              AI voice charting
            </Button>

            <div className="border-l h-6 mx-2" />

            {/* Save and Finalize */}
            <Button
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              variant="outline"
              size="sm"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>

            <Button
              onClick={() => setShowFinalizeModal(true)}
              disabled={isSaving}
              variant="primary"
              size="sm"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Finalize
            </Button>
          </div>
        </div>
      </div>

      {/* Voice Recording Panel */}
      {showVoiceRecording && (
        <Modal
          isOpen={showVoiceRecording}
          onClose={() => setShowVoiceRecording(false)}
          title="Voice Recording"
        >
          <VoiceRecordingPanel
            onSubmit={handleVoiceRecordingSubmit}
            onCancel={() => setShowVoiceRecording(false)}
            isSubmitting={false}
          />
        </Modal>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Tooth Grid */}
        <div className="flex-1 overflow-auto p-6">
          <ToothGrid />
        </div>

        {/* Side Panel - Input Controls Only */}
        <div className="w-80 border-l bg-white overflow-auto">
          {selectedTooth ? (
            <SiteInput toothNumber={selectedTooth} />
          ) : (
            <div className="p-6">
              {/* Chart Controls - includes Clinical Notes, AAP Classification, etc. */}
              <ChartControls />
            </div>
          )}
        </div>
      </div>

      {/* Finalize Confirmation Modal */}
      <Modal
        isOpen={showFinalizeModal}
        onClose={() => setShowFinalizeModal(false)}
        title="Finalize Chart"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to finalize this chart? Once finalized, the chart cannot be edited.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              onClick={() => setShowFinalizeModal(false)}
              variant="outline"
              size="md"
            >
              Cancel
            </Button>
            <Button
              onClick={handleFinalize}
              variant="primary"
              size="md"
              disabled={isSaving}
            >
              {isSaving ? 'Finalizing...' : 'Finalize Chart'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
